import {
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";
import ActionButton from "components/atoms/ActionButton";
import Button from "components/atoms/Button";
import InputDate from "components/atoms/InputDate";
import InputField from "components/atoms/InputField";
import InputSelect from "components/atoms/InputSelect";
import InputDateTime from "components/InputDateTime";
import TableDataComponent from "components/organisms/TableData";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { confirmAlert } from "react-confirm-alert";
import { PlusCircle } from "react-feather";
import { toast } from "react-toastify";
import KelambuDistribution from "services/KelambuDistribution";
import Wilayah from "services/Wilayah";
import { errorToastNotif, setDataToLocalStorage } from "utils/common";
import { PageMode, Source } from "utils/constant";

const WilayahService = new Wilayah();
const KelambuDistributions = new KelambuDistribution();

const Table = ({ changePageMode, onSelectData }) => {
  const [filterState, setFilterState] = useState({
    year: "",
    province_id: "",
  });
  const [searchKeyword, setSearchKeyword] = useState("");
  const [provincies, setProvincies] = useState([]);
  const [kelambuState, setKelambuState] = useState({
    page: 1,
    limit: 10,
    data: [],
    totalData: 0,
    currentPage: 1,
    nextPage: null,
    previousPage: null,
  });
  const [isLoading, setIsLoading] = useState(false);

  const fetchProvincies = async () => {
    try {
      const response = await WilayahService.getAll(Source.PROVINCE, {
        page: 1,
        limit: 9999,
      });
      setProvincies(response.province);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchKelambuDistributions = async () => {
    setIsLoading(true);
    try {
      const params = {
        page: kelambuState.page,
        limit: kelambuState.limit,
        search: searchKeyword,
        year: filterState.year,
        province_id: filterState.province_id,
      };

      Object.keys(params).forEach(
        (key) => params[key] === "" && delete params[key]
      );

      const response = await KelambuDistributions.getAll(params);

      setKelambuState((prevState) => ({
        ...prevState,
        data: response.kelambu_distributions,
        limit: response.limit,
        currentPage: response.currentPage,
        totalData: response.total,
        nextPage: response.nextPage,
        previousPage: response.previousPage,
      }));
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchProvincies();
  }, []);

  useEffect(() => {
    fetchKelambuDistributions();
  }, [kelambuState.page, searchKeyword]);

  const handleApplyFilter = () => {
    setKelambuState((prevState) => ({
      ...prevState,
      page: 1,
    }));
    // Fetch data with current filterState
    setTimeout(() => {
      fetchKelambuDistributions();
    }, 0);
  };

  const onPageChange = (page) => {
    setKelambuState((prevState) => ({
      ...prevState,
      page: page,
    }));
  };

  const onEditData = (data) => {
    onSelectData(data);
  };

  const onRemoveData = (id) => {
    confirmAlert({
      title: "Apakah anda yakin ingin menghapus data?",
      message: "Data akan dihapus secara permanen",
      buttons: [
        {
          label: "Ya, Hapus!",
          onClick: async () => {
            await KelambuDistributions.remove(id)
              .then((res) => {
                fetchKelambuDistributions();
                toast.success("Data Kelambu Nasional Berhasil Dihapus", {
                  position: "top-right",
                  autoClose: 5000,
                  hideProgressBar: false,
                  closeOnClick: true,
                  pauseOnHover: true,
                  theme: "light",
                });
              })
              .catch((err) => {
                console.log(err);
                errorToastNotif(err);
              });
          },
        },
        {
          label: "Tidak",
        },
      ],
    });
  };

  const columns = [
    {
      header: "Aksi",
      accessorKey: "action",
      cell: ({ row }) => (
        <div className="button-actions">
          <ActionButton
            onClick={() => onEditData(row.original)}
            btnType={"edit"}
          />
          <ActionButton
            onClick={() => onRemoveData(row.original.id)}
            btnType={"delete"}
          />
        </div>
      ),
    },
    {
      header: "Tahun",
      accessorKey: "year",
    },
    {
      header: "Provinsi",
      accessorKey: "province_id",
      cell: ({ row }) => row.original.province_id?.split("|")[1],
    },
    {
      header: "Kabupaten/Kota",
      accessorKey: "district_id",
      cell: ({ row }) => row.original.district_id?.split("|")[1],
    },
    {
      header: "Kecamatan",
      accessorKey: "subdistrict_id",
      cell: ({ row }) => row.original.subdistrict_id?.split("|")[1],
    },
    {
      header: "Kelurahan/Desa",
      accessorKey: "urbanvillage_id",
      cell: ({ row }) => row.original.urbanvillage_id?.split("|")[1],
    },
    {
      header: "Total Penduduk Sasaran",
      accessorKey: "target_population",
    },
    {
      header: "Kelambu Massal",
      accessorKey: "total_mass_mosquito_nets",
    },
    {
      header: "Kelambu Massal Fokus",
      accessorKey: "mass_focus_mosquito_nets",
    },
  ];

  const tableInstance = useReactTable({
    columns,
    data: kelambuState.data,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
      <h1>Data Dasar Kelambu</h1>

      <div className="card-filter">
        <div className="filter-item">
          <InputDate
            placeholder={"Pilih Tahun"}
            name={"selected_year"}
            dateFormat={"YYYY"}
            onChange={(e) => {
              setFilterState((prev) => ({
                ...prev,
                year: moment(e).format("YYYY"),
              }));
            }}
            value={filterState.year}
          />
        </div>
        <div className="filter-item">
          <InputSelect
            placeholder={"Pilih Provinsi"}
            data={provincies.map((item) => {
              return {
                label: item.name,
                value: `${item.id}|${item.name}`,
              };
            })}
            onChange={(e) => {
              const selectedValue = e ? e.value : "";
              setFilterState((prev) => ({
                ...prev,
                province_id: selectedValue,
              }));
            }}
            value={provincies
              .map((item) => {
                return {
                  label: item.name,
                  value: `${item.id}|${item.name}`,
                };
              })
              .find((item) => item.value === filterState.province_id)}
          />
        </div>
        <Button
          variant={"primary"}
          size={"normal"}
          onClick={handleApplyFilter}
          isDisabled={isLoading}
        >
          Terapkan
        </Button>
      </div>
      <div className="action-wrapper">
        <div className="action-button">
          <Button
            variant={"primary"}
            size={"normal"}
            onClick={() => {
              changePageMode(PageMode.add);
            }}
          >
            <PlusCircle />
            Tambah Data
          </Button>
        </div>
        <div className="action-search">
          <InputField
            placeholder={"Cari berdasarkan nama desa"}
            onChange={(e) => setSearchKeyword(e.target.value)}
            value={searchKeyword}
          />
        </div>
      </div>

      <TableDataComponent
        columnLength={columns?.length}
        tableInstance={tableInstance}
        onPageChange={onPageChange}
        loading={isLoading}
        currentPage={kelambuState.currentPage}
        limit={kelambuState.limit}
        nextPage={kelambuState.nextPage}
        previousPage={kelambuState.previousPage}
        total={kelambuState.totalData}
      />
    </div>
  );
};

export default Table;
